<script setup lang="ts">
import type {
  InsightConfig,
  InsightConfigJsonField,
  InsightConfigMeta,
} from '../../../models/insightConfig.types'
import { INSIGHT_CONFIG_JSON_FIELD_SPECS } from '../../../service/insightConfig'
import type { PipelineStepEditor } from '../../../service/insightConfigPipeline'

import InsightConfigPipelineBuilder from './InsightConfigPipelineBuilder.vue'

import { useI18n } from '@/shared/i18n'
import { JsonField } from '@/shared/ui/json-field'

defineModel<InsightConfig>('form', { required: true })
const json = defineModel<Record<InsightConfigJsonField, string>>('json', { required: true })
const pipeline = defineModel<PipelineStepEditor[]>('pipeline', { required: true })
const props = defineProps<{
  meta: InsightConfigMeta
  errors: Record<string, string[]>
  warning?: boolean
  disabled?: boolean
}>()
const { t } = useI18n()
const error = () =>
  (props.errors['llm_config'] ?? [])
    .map((message) => (message.startsWith('insights.') ? t(message) : message))
    .join(' ')
</script>

<template>
  <fieldset class="config-section grid gap-md min-w-0 p-lg border border-border rounded-lg">
    <legend>{{ t('insights.sysadmin.config.section.pipeline') }}</legend>
    <InsightConfigPipelineBuilder
      v-model="pipeline"
      :meta="meta"
      :errors="errors"
      :warning="warning"
      :disabled="disabled"
    />
    <JsonField
      :model-value="json.llm_config"
      :spec="INSIGHT_CONFIG_JSON_FIELD_SPECS.llm_config"
      :label="t('insights.sysadmin.config.fields.llmConfig')"
      :description="t('insights.sysadmin.config.jsonDescription.llmConfig')"
      :error-message="error()"
      :null-label="t('insights.sysadmin.config.setNull')"
      :default-label="t('insights.sysadmin.config.useDefault')"
      :disabled="disabled"
      @update:model-value="json = { ...json, llm_config: $event }"
    />
  </fieldset>
</template>

<style scoped>
.config-section legend {
  padding-inline: var(--ui-space-xs);
  font-weight: 600;
}
</style>
